import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/layout/Layout';
import ErrorBoundary from './components/common/ErrorBoundary';
import { scheduleRoutePrefetch } from './utils/routePrefetch';

const Home = lazy(() => import('./pages/Home'));
const Explore = lazy(() => import('./pages/Explore'));
const CampaignDetail = lazy(() => import('./pages/CampaignDetail'));
const Guarantee = lazy(() => import('./pages/Guarantee'));
const PaymentCallback = lazy(() => import('./pages/PaymentCallback'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Login = lazy(() => import('./pages/Auth/Login'));
const Register = lazy(() => import('./pages/Auth/Register'));
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'));
const About = lazy(() => import('./pages/Legal/About'));
const Terms = lazy(() => import('./pages/Legal/Terms'));
const Privacy = lazy(() => import('./pages/Legal/Privacy'));
const Faq = lazy(() => import('./pages/Legal/Faq'));

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Overview = lazy(() => import('./pages/Dashboard/Overview'));
const MyCampaigns = lazy(() => import('./pages/Dashboard/MyCampaigns'));
const MyContributions = lazy(() => import('./pages/Dashboard/MyContributions'));
const Withdrawals = lazy(() => import('./pages/Dashboard/Withdrawals'));
const Settings = lazy(() => import('./pages/Dashboard/Settings'));
const CreateCampaign = lazy(() => import('./pages/Dashboard/CreateCampaign'));
const EditCampaign = lazy(() => import('./pages/Dashboard/EditCampaign'));

const Admin = lazy(() => import('./pages/Admin'));
const AdminDashboard = lazy(() => import('./pages/Admin/Dashboard'));
const AdminCampaigns = lazy(() => import('./pages/Admin/Campaigns'));
const AdminUsers = lazy(() => import('./pages/Admin/Users'));
const AdminWithdrawals = lazy(() => import('./pages/Admin/Withdrawals'));
const AdminReports = lazy(() => import('./pages/Admin/Reports'));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Guest Route (redirect if already logged in)
function GuestRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Admin Route Component
function AdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user } = useAuth();

  useEffect(() => {
    scheduleRoutePrefetch(
      ['home', 'explore', 'campaignDetail', 'guarantee', 'about', 'terms', 'privacy', 'faq'],
      1200,
    );
  }, []);

  useEffect(() => {
    if (!user) {
      scheduleRoutePrefetch(['login', 'register', 'forgotPassword'], 1800);
      return;
    }

    const memberRoutes = [
      'dashboard',
      'overview',
      'myCampaigns',
      'myContributions',
      'withdrawals',
      'settings',
      'createCampaign',
      'editCampaign',
      'paymentCallback',
    ];

    if (user.is_admin) {
      memberRoutes.push(
        'admin',
        'adminDashboard',
        'adminCampaigns',
        'adminUsers',
        'adminWithdrawals',
        'adminReports',
      );
    }

    scheduleRoutePrefetch(memberRoutes, 1800);
  }, [user]);

  return (
    <ErrorBoundary>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="explore" element={<Explore />} />
          <Route path="campaign/:slug" element={<CampaignDetail />} />
          <Route path="garantie" element={<Guarantee />} />
          <Route path="payment/callback" element={<PaymentCallback />} />
          <Route path="about" element={<About />} />
          <Route path="terms" element={<Terms />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="faq" element={<Faq />} />

          {/* Auth Routes (Guest only) */}
          <Route
            path="login"
            element={
              <GuestRoute>
                <Login />
              </GuestRoute>
            }
          />
          <Route
            path="register"
            element={
              <GuestRoute>
                <Register />
              </GuestRoute>
            }
          />
          <Route
            path="forgot-password"
            element={
              <GuestRoute>
                <ForgotPassword />
              </GuestRoute>
            }
          />

          {/* Dashboard Routes (Protected) */}
          <Route
            path="dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          >
            <Route index element={<Overview />} />
            <Route path="campaigns" element={<MyCampaigns />} />
            <Route path="contributions" element={<MyContributions />} />
            <Route path="withdrawals" element={<Withdrawals />} />
            <Route path="settings" element={<Settings />} />
            <Route path="create" element={<CreateCampaign />} />
            <Route path="campaigns/:id/edit" element={<EditCampaign />} />
          </Route>

          {/* Admin Routes */}
          <Route
            path="admin"
            element={
              <AdminRoute>
                <Admin />
              </AdminRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="campaigns" element={<AdminCampaigns />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="withdrawals" element={<AdminWithdrawals />} />
            <Route path="reports" element={<AdminReports />} />
          </Route>
        </Route>

        {/* 404 */}
        <Route path="*" element={<Layout />}>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Toaster position="top-right" richColors closeButton />
          <Suspense fallback={<RouteFallback />}>
            <AppRoutes />
          </Suspense>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
